import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';
import { External } from '../decorators/app.decorators';
import { EnvironmentService } from './environment.service';
import { DataProviderConfigService } from './data-provider-config.service';
import { SILENT_REQUEST } from '../interceptors/silent-request.token';
import { GetComponents, GetWorkloadInputConfigiuration } from '../../store/project-details/project-details.actions';
import { DataProvider, DOMAIN_TYPE, getInputConfigFromDataProviderAPIResponse } from 'pcs-domain';
import { ReadyNodeHardwareBounds } from '../../feature-modules/readynode-selector/readynode-selector.component';
import {
  QuickSizerApiRequest,
  QuickSizerApiResponse,
  QuickSizerConfigs,
  QS_DATAPROVIDER_COMPONENTS_ARCHITECTURE,
  QS_DATAPROVIDER_DEPLOYMENT_CODE,
  QS_DATAPROVIDER_PROFILE_MODE_CODE,
  QS_DATAPROVIDER_STORAGE_TYPE,
  QS_DATAPROVIDER_WORKLOAD_OPTION,
  QS_PRODUCT_NAME,
  QS_PRODUCT_VERSION,
  QS_VSAN_ESA,
} from '../../feature-modules/quick-sizer/models/quick-sizer.models';

@External()
@Injectable({ providedIn: 'root' })
export class QuickSizerService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(EnvironmentService);
  private readonly dataProvider = inject(DataProviderConfigService);
  private configs$?: Observable<QuickSizerConfigs>;
  private hardwareBounds$?: Observable<ReadyNodeHardwareBounds>;

  /** Errors are shown inline by the wizard, so the global error toast is skipped. */
  private readonly silent = { context: new HttpContext().set(SILENT_REQUEST, true) };

  getConfigs(): Observable<QuickSizerConfigs> {
    this.configs$ ??= this.http.get<QuickSizerConfigs>(
      `${this.env.config.dataProviderAPIUrl}products/${QS_PRODUCT_NAME}/versions/${QS_PRODUCT_VERSION}`
        + `/quicksizer/configs?vsanArchitecture=${QS_VSAN_ESA}`,
      this.silent,
    ).pipe(shareReplay(1));
    return this.configs$;
  }

  getRecommendation(request: QuickSizerApiRequest): Observable<QuickSizerApiResponse> {
    return this.http.post<QuickSizerApiResponse>(
      `${this.env.config.seApiEndpoint}/quicksizer/recommendation`,
      request,
      this.silent,
    );
  }

  getManagementComponents(): Observable<DataProvider.Component[]> {
    return this.dataProvider.getComponents(new GetComponents(
      QS_PRODUCT_NAME,
      QS_PRODUCT_VERSION,
      'MANAGEMENT',
      QS_DATAPROVIDER_COMPONENTS_ARCHITECTURE,
    )) as Observable<DataProvider.Component[]>;
  }

  /** ReadyNode customization ceilings, from the same catalog call and adapter the forward sizer uses. */
  getHardwareBounds(): Observable<ReadyNodeHardwareBounds> {
    this.hardwareBounds$ ??= this.dataProvider.getWorkloadInputConfiguration(
      new GetWorkloadInputConfigiuration(
        QS_PRODUCT_NAME,
        QS_PRODUCT_VERSION,
        DOMAIN_TYPE.WorkloadDomain,
        undefined,
        QS_DATAPROVIDER_STORAGE_TYPE,
        QS_VSAN_ESA,
        QS_DATAPROVIDER_WORKLOAD_OPTION,
        QS_DATAPROVIDER_DEPLOYMENT_CODE,
        QS_DATAPROVIDER_PROFILE_MODE_CODE,
      ),
    ).pipe(
      map(res => getInputConfigFromDataProviderAPIResponse(
        res as DataProvider.InputConfigurationResponse,
      ).hardwareConfiguration),
      shareReplay(1),
    );
    return this.hardwareBounds$;
  }
}
